/**
 * Los «Datos del cliente» de la pestaña Ficha, fila por fila.
 *
 * Primero lo que el sistema sabe de la PERSONA —su teléfono, su usuario de
 * WhatsApp—, después el cuestionario del bot y al final lo que la IA detectó en
 * los documentos. El orden es el de la confianza: lo detectado en un documento
 * es una lectura de ese archivo y el asesor tiene que confirmarlo.
 *
 * DEF-018 (QA de TEST, 02-oct-2026): la fila «Teléfono» era `detail.phone`, la
 * DIRECCIÓN de envío, que para un cliente con alias es un BSUID. Ahora sale de
 * `telefonoDeFicha`, y el usuario de WhatsApp va en su propia fila.
 *
 * DEF-024 (mismo QA): el nombre del cliente era el «Nombre detectado» del
 * documento más nuevo, y tras una Copia Literal mal leída la Ficha decía que
 * el cliente se llamaba «Por Escritura Publica». El nombre y el DNI salen
 * ahora de `detail.solicitante`, que el backend arma con la precedencia de la
 * conversación; el «Nombre detectado» de cada documento se queda en la
 * pestaña Documentos, que es donde dice de qué archivo salió.
 *
 * Puro a propósito, para que el test lo corra en Node sobre el detalle que
 * devuelve el backend (`tests/unit/portal_ts.py`). Solo importa otro módulo
 * puro; los tipos se borran al transpilar.
 */
import { telefonoDeFicha } from './identidad'

/** De dónde sale cada fila, de más a menos confiable. */
export type OrigenDeFila = 'caso' | 'cuestionario' | 'documento'

export interface FilaDeFicha {
  clave: string
  valor: string
  origen: OrigenDeFila
  /** De dónde salió el dato, cuando no es obvio («de su DNI»). */
  detalle?: string
}

/** El rótulo con el que el backend (`_resumen_documento`) pone el
 *  `person_name` de cada documento. Es una lectura de ESE archivo —de una
 *  Copia Literal sale «Por Escritura Publica»—, no el nombre del cliente. */
export const NOMBRE_DETECTADO = 'Nombre detectado'

/** `fuente` de `solicitante`, en palabras del asesor. */
const ORIGEN_DEL_NOMBRE: Record<string, string> = {
  registro: 'del registro del caso',
  dni: 'de su DNI',
}

/** Lo que la ficha lee del detalle. Un subconjunto, para poder probarla con
 *  el JSON que devuelve el backend sin armar un `ConversationDetail` entero. */
export interface DatosDeFicha {
  phone?: string | null
  contact_phone?: string | null
  bsuid?: string | null
  whatsapp_username?: string | null
  solicitante?: { nombre?: string; fuente?: string; dni?: string } | null
  basic_data?: Record<string, unknown> | null
  inmueble_declarado?: {
    ubicacion?: string; tipo?: string; inscrito_sunarp?: string; area_m2?: string
  } | null
  inmueble_elegido?: { partida?: string; direccion?: string; ubicacion?: string } | null
  documents?: Array<{
    extracted_fields?: Record<string, string> | null
    status?: string
    version_preferida?: string
    otro_inmueble?: string
  }> | null
}

const TIPO_DE_INMUEBLE: Record<string, string> = {
  casa: 'Casa', departamento: 'Departamento', terreno: 'Terreno',
  local: 'Local', edificio: 'Edificio',
}
const EN_SUNARP: Record<string, string> = { si: 'Sí', no: 'No', no_sabe: 'No sabe' }

export function filasDeLaFicha(d: DatosDeFicha): FilaDeFicha[] {
  const solicitante = d.solicitante ?? {}
  const nombre = (solicitante.nombre ?? '').trim()
  // Un dato que falta se marca, no se omite: sin la fila el asesor no sabe si
  // el nombre no existe o si nadie lo cargó.
  const filas: FilaDeFicha[] = [
    { clave: 'Solicitante', valor: nombre || 'No identificado todavía', origen: 'caso',
      detalle: nombre ? ORIGEN_DEL_NOMBRE[solicitante.fuente ?? ''] : undefined },
    { clave: 'DNI del solicitante', valor: (solicitante.dni ?? '').trim() || 'No disponible',
      origen: 'caso' },
    { clave: 'Teléfono', valor: telefonoDeFicha(d), origen: 'caso' },
  ]
  const usuario = (d.whatsapp_username ?? '').trim()
  if (usuario) filas.push({ clave: 'Usuario de WhatsApp', valor: usuario, origen: 'caso' })

  const vistas = new Set(filas.map((f) => f.clave.toLowerCase()))
  const agregar = (clave: string, valor: unknown, origen: OrigenDeFila) => {
    if (!valor || vistas.has(clave.toLowerCase())) return
    vistas.add(clave.toLowerCase())
    filas.push({ clave, valor: String(valor), origen })
  }

  // Los datos del cuestionario suelen estar vacíos (el bot puede tenerlo
  // apagado). Lo que sí hay casi siempre es lo que la IA detectó en los
  // documentos, así que se completa con eso en vez de mostrar la ficha vacía.
  for (const [clave, valor] of Object.entries(d.basic_data ?? {})) {
    agregar(clave, valor, 'cuestionario')
  }
  // Casos 3.1–3.5: lo que DECLARÓ en el precalificador del socio. Es una
  // respuesta del cliente, igual que el cuestionario; contra esto se compara el
  // inmueble que elige cuando manda Copias Literales de más de uno.
  const declarado = d.inmueble_declarado ?? {}
  const tipo = TIPO_DE_INMUEBLE[declarado.tipo ?? ''] ?? (declarado.tipo ?? '')
  const lugar = (declarado.ubicacion ?? '').trim()
  if (lugar || tipo) {
    filas.push({ clave: 'Inmueble declarado',
                 valor: [tipo, lugar].filter(Boolean).join(' en '),
                 origen: 'cuestionario', detalle: 'en el precalificador' })
    vistas.add('inmueble declarado')
  }
  if (declarado.inscrito_sunarp) {
    agregar('Inscrito en SUNARP (declarado)',
            EN_SUNARP[declarado.inscrito_sunarp] ?? declarado.inscrito_sunarp, 'cuestionario')
  }
  if (declarado.area_m2) agregar('Área declarada', `${declarado.area_m2} m²`, 'cuestionario')
  const elegido = d.inmueble_elegido ?? {}
  if (elegido.partida) {
    filas.push({ clave: 'Inmueble elegido',
                 valor: [`Partida ${elegido.partida}`, elegido.ubicacion].filter(Boolean).join(' · '),
                 origen: 'caso', detalle: 'entre las Copias Literales que mandó' })
    vistas.add('inmueble elegido')
  }
  // Del más nuevo al más viejo: si dos documentos traen el mismo campo, gana
  // el más reciente. Menos el nombre detectado (DEF-024): ése no es un dato
  // del cliente sino del archivo, y el del cliente ya está arriba.
  //
  // Y solo de los documentos que se USAN (DEF-045, QA de TEST del 05-oct-2026):
  // uno rechazado por el asesor o una versión reemplazada (§9.15) no es dato
  // del caso. Con «el más reciente» a secas, una Copia Literal equivocada que
  // el asesor rechazó le ponía su partida al caso por haber llegado última.
  for (const doc of d.documents ?? []) {
    if (doc.status === 'REJECTED' || doc.version_preferida === 'no') continue
    // Casos 3.1–3.5: un documento del inmueble que el cliente NO eligió no es
    // dato del caso — su partida no es la del expediente.
    if (doc.otro_inmueble) continue
    for (const [clave, valor] of Object.entries(doc.extracted_fields ?? {})) {
      if (clave === NOMBRE_DETECTADO) continue
      agregar(clave, valor, 'documento')
    }
  }
  return filas
}
